import { Menu, MenuItem } from "@mui/material";
import { MuiBox, MuiTypography, SubmitLoaderButton } from "components/atoms";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "components/ui";
import { EllipsisVertical, Eye, CheckCircle, Clock } from "lucide-react";

import { UpdateReportAction } from "store/middlewares/reportProblem";
import checkAccess from "utils/checkAccess";
import { openAdminModal, closeAdminModal } from "store/slices/common/adminModal.slice";
import ModalReportProblem from "components/molecules/ModalReportProblem";

/* ---------- status change confirm (same file) ---------- */
const UpdateReportStatusConfirm = ({ row, filter, newStatus, isSolving }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUpdate = () => {
    setIsSubmitting(true);
    dispatch(
      UpdateReportAction({
        id: row.id,
        type: newStatus,
        filter,
        onSuccess: () => {
          dispatch(closeAdminModal());
          setIsSubmitting(false);
        },
        onError: () => {
          dispatch(closeAdminModal());
          setIsSubmitting(false);
        },
      })
    );
  };

  return (
    <div className="grid gap-5 p-1">
      {/* Title with icon */}
      <div className="flex items-center gap-3.5">
        <div
          className={`flex size-11 shrink-0 items-center justify-center rounded-full ${
            isSolving
              ? "bg-blue-100 text-blue-600"
              : "bg-amber-100 text-amber-600"
          }`}
        >
          {isSolving ? (
            <CheckCircle className="size-5" aria-hidden="true" />
          ) : (
            <Clock className="size-5" aria-hidden="true" />
          )}
        </div>
        <h2 className="text-xl font-bold text-foreground">
          {isSolving
            ? "Mark this request as solved?"
            : "Mark this request as pending?"}
        </h2>
      </div>

      {/* What happens */}
      <ul className="grid gap-2">
        <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
          <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
          <span className="break-words">
            This report problem status will be updated to{" "}
            <strong>{newStatus}</strong>.
          </span>
        </li>
        <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
          <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
          <span>
            {isSolving
              ? "The user will be notified that their issue has been resolved."
              : "The issue will be marked as unresolved and may need further action."}
          </span>
        </li>
        <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
          <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
          <span>You can change this status at any time.</span>
        </li>
      </ul>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          size="lg"
          disabled={isSubmitting}
          onClick={() => dispatch(closeAdminModal())}
        >
          Cancel
        </Button>
        <SubmitLoaderButton
          type="button"
          variant={isSolving ? "primary" : "destructive"}
          size="lg"
          btnText={`Mark as ${newStatus}`}
          isDisable={isSubmitting}
          onClick={handleUpdate}
        />
      </div>
    </div>
  );
};
/* ------------------------------------------------------- */

const ReportProblemActionBar = ({ row, filter }) => {
  const dispatch = useDispatch();

  //--------------------------------------------------- Action Menu Array --------------------------------------
  const ActionMenuArray = [
    checkAccess("update_report_problem") && {
      label: row.status === "solved" ? "Mark as Pending" : "Mark as Solved",
      icon:
        row.status === "solved" ? (
          <Clock size={16} color="#475569" />
        ) : (
          <CheckCircle size={16} color="#475569" />
        ),
      onClick: () => {
        const isSolving = row.status !== "solved";
        const newStatus = isSolving ? "solved" : "pending";

        dispatch(
          openAdminModal({
            title: "",
            content: (
              <UpdateReportStatusConfirm
                row={row}
                filter={filter}
                newStatus={newStatus}
                isSolving={isSolving}
              />
            ),
          })
        );
      },
    },
    {
      label: "View Details",
      icon: <Eye size={16} color="#475569" />,
      onClick: () => {
        dispatch(
          openAdminModal({
            title: "Report Problem Detail",
            content: <ModalReportProblem row={row} />,
          })
        );
      },
    },
  ].filter(Boolean);

  // For action menu
  const [actionMenu, setActionMenu] = useState(null);
  const handleActionMenuOpen = (event) => {
    setActionMenu(event.currentTarget);
  };
  const handleActionMenuClose = () => {
    setActionMenu(null);
  };

  return (
    <MuiBox className="flex h-full items-center justify-center gap-3">
      <Button
        className="size-7"
        mode="icon"
        variant="ghost"
        onClick={handleActionMenuOpen}
      >
        <EllipsisVertical />
      </Button>
      <Menu
        anchorEl={actionMenu}
        open={Boolean(actionMenu)}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          " & .MuiMenu-paper": {
            mt: "10px",
            background: "#fff",
            boxShadow:
              "0 4px 6px -1px #e2e8f0, 0 2px 4px -2px #e2e8f0 !important",
          },
        }}
      >
        {ActionMenuArray.map((option) => (
          <MenuItem
            key={option.label}
            onClick={() => {
              handleActionMenuClose();
              option.onClick();
            }}
          >
            <MuiBox sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
              <MuiBox sx={{ width: "fit-content" }}>{option.icon}</MuiBox>
              <MuiTypography
                sx={{ fontWeight: "500", fontSize: "13px", color: "#475569" }}
              >
                {option.label}
              </MuiTypography>
            </MuiBox>
          </MenuItem>
        ))}
      </Menu>
    </MuiBox>
  );
};

export default ReportProblemActionBar;